/**
 * useAutoRetry — Exponential backoff auto-retry with countdown
 *
 * Backoff schedule: 5s → 10s → 30s
 * After maxRetries (default 3): stops auto-retrying, manual only.
 *
 * Uses refs for timer logic to avoid React batching issues with fake timers
 * in tests. State is synced for display on each tick.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const BACKOFF_SCHEDULE = [5, 10, 30] as const;

interface UseAutoRetryOptions {
  onRetry: () => void;
  maxRetries?: number;
}

interface UseAutoRetryResult {
  /** Number of retries completed so far */
  retryCount: number;
  /** Current countdown in seconds (0 when not counting down) */
  countdown: number;
  /** Total duration of the current countdown phase */
  countdownDuration: number;
  /** Whether auto-retry is paused and only manual retry is available */
  manualRetryOnly: boolean;
  /** Whether the countdown is actively running */
  isCountingDown: boolean;
  /** Trigger a manual retry (also works after max retries) */
  retry: () => void;
}

function getBackoffDuration(count: number): number {
  return BACKOFF_SCHEDULE[Math.min(count, BACKOFF_SCHEDULE.length - 1)];
}

interface InternalState {
  countdown: number;
  countdownDuration: number;
  retryCount: number;
}

export function useAutoRetry({
  onRetry,
  maxRetries = 3,
}: UseAutoRetryOptions): UseAutoRetryResult {
  const initialDuration = getBackoffDuration(0);
  const countdownRef = useRef(initialDuration);
  const retryCountRef = useRef(0);
  const durationRef = useRef(initialDuration);
  const onRetryRef = useRef(onRetry);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [display, setDisplay] = useState<InternalState>({
    countdown: initialDuration,
    countdownDuration: initialDuration,
    retryCount: 0,
  });

  // Keep onRetry ref current
  useEffect(() => {
    onRetryRef.current = onRetry;
  }, [onRetry]);

  const syncDisplay = useCallback(() => {
    setDisplay({
      countdown: countdownRef.current,
      countdownDuration: durationRef.current,
      retryCount: retryCountRef.current,
    });
  }, []);

  // Run the countdown interval
  useEffect(() => {
    if (retryCountRef.current >= maxRetries) return;

    intervalRef.current = setInterval(() => {
      if (countdownRef.current <= 0 || retryCountRef.current >= maxRetries) {
        return;
      }

      countdownRef.current -= 1;

      if (countdownRef.current <= 0) {
        // Countdown exhausted — fire retry
        onRetryRef.current();
        retryCountRef.current += 1;

        if (retryCountRef.current < maxRetries) {
          const nextDuration = getBackoffDuration(retryCountRef.current);
          durationRef.current = nextDuration;
          countdownRef.current = nextDuration;
        }
      }

      syncDisplay();
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [maxRetries, syncDisplay]);

  const retry = useCallback(() => {
    onRetryRef.current();
    retryCountRef.current += 1;

    if (retryCountRef.current < maxRetries) {
      const nextDuration = getBackoffDuration(retryCountRef.current);
      durationRef.current = nextDuration;
      countdownRef.current = nextDuration;
    }

    syncDisplay();
  }, [maxRetries, syncDisplay]);

  const manualRetryOnly = display.retryCount >= maxRetries;
  const isCountingDown = !manualRetryOnly && display.countdown > 0;

  return {
    retryCount: display.retryCount,
    countdown: display.countdown,
    countdownDuration: display.countdownDuration,
    manualRetryOnly,
    isCountingDown,
    retry,
  };
}
